
import apiClient from '../utils/axios-config';
import { apiCall } from '../utils/apiUtils';

// Enhanced API call with CORS error handling
const apiCallWithCORSHandling = async (url: string, options: any = {}): Promise<any> => {
  try {
    const response = await apiCall(url, options);
    return response;
  } catch (error: any) {
    console.warn('API call failed, possibly due to CORS:', error);
    
    // Handle CORS errors with proper retry logic
    if (error.message?.includes('CORS') || 
        error.message?.includes('blocked') ||
        error.message?.includes('preflight') ||
        error.name === 'TypeError') {
      
      console.log('🔄 CORS error detected, implementing retry with proper headers');
      
      // Retry with proper CORS headers
      try {
        const retryResponse = await apiClient.get(url, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        return retryResponse.data;
      } catch (retryError) {
        console.error('Retry failed, API endpoint may be unavailable:', retryError);
        throw retryError;
      }
    }
    
    throw error;
  }
};

const API_BASE_URL = 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

export interface TargetingData {
  demographics: {
    ageRange: [number, number];
    gender: string;
    location: string;
    interests: string[];
  };
  behavioral: {
    purchaseHistory: string[];
    browsingPatterns: string[];
    engagementLevel: 'high' | 'medium' | 'low';
  };
  contextual: {
    currentPage: string;
    timeOfDay: string;
    deviceType: string;
    seasonality: string;
  };
}

export interface TargetingContext {
  userId?: string;
  outfitData?: any;
  pageContext: string;
  pageType?: string;
  user?: any;
  userPreferences?: any;
  searchQuery?: string;
  socialContext?: any;
}

export class TargetingService {
  private static instance: TargetingService;
  private targetingCache: Map<string, { data: TargetingData; timestamp: number }> = new Map();
  private pendingRequests: Map<string, Promise<TargetingData>> = new Map();
  private readonly CACHE_TTL = 60000; // 1 minute cache
  
  public static getInstance(): TargetingService {
    if (!TargetingService.instance) {
      TargetingService.instance = new TargetingService();
    }
    return TargetingService.instance;
  }

  async getTargetingData(context: TargetingContext): Promise<TargetingData> {
    try {
      const response = await apiCallWithCORSHandling(`${API_BASE_URL}/targeting/data`, {
        method: 'POST',
        body: JSON.stringify(context)
      });
      return response.targetingData;
    } catch (error) {
      console.error('Error fetching targeting data:', error);
      return this.getDefaultTargetingData();
    }
  }

  async updateUserTargeting(userId: string, targetingData: Partial<TargetingData>): Promise<boolean> {
    try {
      await apiCallWithCORSHandling(`${API_BASE_URL}/targeting/user/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(targetingData)
      });
      return true;
    } catch (error) {
      console.error('Error updating user targeting:', error);
      return false;
    }
  }

  async getRecommendedAds(targetingData: TargetingData, limit: number = 5): Promise<any[]> {
    try {
      const response = await apiCallWithCORSHandling(`${API_BASE_URL}/targeting/recommended-ads`, {
        method: 'POST',
        body: JSON.stringify({ targetingData, limit })
      });
      return response.ads || [];
    } catch (error) {
      console.error('Error fetching recommended ads:', error);
      return [];
    }
  }

  async generateTargeting(context: TargetingContext): Promise<TargetingData> {
    // Create cache key from context
    const cacheKey = JSON.stringify({
      userId: context.userId,
      outfitId: context.outfitData?.id,
      pageType: context.pageType,
      searchQuery: context.searchQuery
    });
    
    // Check cache first
    const cached = this.targetingCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      console.log('📦 Using cached targeting data');
      return cached.data;
    }
    
    // Check if there's already a pending request for this context
    const pending = this.pendingRequests.get(cacheKey);
    if (pending) {
      console.log('⏳ Waiting for pending targeting request');
      return pending;
    }
    
    // Create new request
    const requestPromise = (async () => {
      try {
        const response = await apiCallWithCORSHandling(`${API_BASE_URL}/targeting/generate`, {
          method: 'POST',
          body: JSON.stringify(context)
        });
        
        const targetingData = response.targetingData || this.getDefaultTargetingData();
        
        // Cache the result
        this.targetingCache.set(cacheKey, {
          data: targetingData,
          timestamp: Date.now()
        });
        
        return targetingData;
      } catch (error) {
        console.error('Error generating targeting:', error);
        return this.getDefaultTargetingData();
      } finally {
        // Remove from pending requests
        this.pendingRequests.delete(cacheKey);
      }
    })();
    
    // Store pending request
    this.pendingRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }

  private getDefaultTargetingData(): TargetingData {
    return {
      demographics: {
        ageRange: [18, 65],
        gender: 'all',
        location: 'global',
        interests: []
      },
      behavioral: {
        purchaseHistory: [],
        browsingPatterns: [],
        engagementLevel: 'medium'
      },
      contextual: {
        currentPage: 'unknown',
        timeOfDay: new Date().getHours() < 12 ? 'morning' : 'afternoon',
        deviceType: 'desktop',
        seasonality: 'all'
      }
    };
  }
}

export const targetingService = TargetingService.getInstance();
